import { Link } from '@remix-run/react'
import {
  LucideBarChart4,
  LucideCircleDotDashed,
  LucideEye,
  LucideEyeOff,
  LucideFileText,
  LucideLineSquiggle,
  LucideScan,
  LucideSheet,
  LucideSquare,
  LucideSquareCheck,
  LucideSquareCheckBig,
  LucideWaypoints,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAdminReportsQuery } from '~/api'
import {
  aCheckedListings,
  aDrawnFeature,
  aHiddenListings,
  aMapFilters,
  aMapViewState,
  aUser,
  useAtom,
  useAtomValue,
} from '~/atoms'
import { ControlTooltip, CreatePdfDialog } from '~/components'
import { Badge, Button, Command, Popover } from '~/components/ui'
import type { MapListingProjection } from '~/models'
import ReportAddToExisting from './report/AddToExisting'

export default function MapReportActions({
  listings,
  currentMapCount,
}: {
  listings: MapListingProjection[]
  currentMapCount: number | null
}) {
  const user = useAtomValue(aUser)
  const [checkedListings, setCheckedListings] = useAtom(aCheckedListings)
  const [hiddenListings, setHiddenListings] = useAtom(aHiddenListings)
  const drawnFeature = useAtomValue(aDrawnFeature)
  const mapViewState = useAtomValue(aMapViewState)
  const mapFilters = useAtomValue(aMapFilters)
  const [isOpen, setIsOpen] = useState(false)

  const { reports } = useAdminReportsQuery({
    key: ['user-reports', user?._id || ''],
    find: {
      'user._id': user?._id,
      deleted: { $ne: true },
      includeIds: { $ne: null },
    },
    refetchInterval: 1000 * 15,
    enabled: !!user,
  })

  const exportTransactionsLink = useMemo(() => {
    const filtersParam = encodeURIComponent(JSON.stringify(mapFilters))
    return `/data/export-transactions.csv?listings=${checkedListings.join(',')}&filters=${filtersParam}`
  }, [checkedListings, mapFilters])

  if (!user) {
    return null
  }

  return (
    <Popover open={isOpen} onOpenChange={(value) => setIsOpen(value)}>
      <ControlTooltip text="Report Actions" side="bottom">
        <Popover.Trigger asChild>
          <Button
            variant="map"
            size="map"
            className="relative w-auto px-3"
            onClick={() => setIsOpen(!isOpen)}>
            <LucideBarChart4 className="h-5 w-5" />
            <span className="ml-2 hidden xl:inline-flex">Reports</span>
            {checkedListings.length > 0 && (
              <Badge className="pointer-events-none absolute -right-1 -top-1 inline-flex h-4 items-center justify-center rounded-full px-1 py-0 text-xs">
                {checkedListings.length}
              </Badge>
            )}
          </Button>
        </Popover.Trigger>
      </ControlTooltip>

      <Popover.Content className={'w-[220px] p-0'} align="start">
        <Command>
          <Command.List className="flex !max-h-none flex-col">
            <Command.Group heading="Create" className="text-foreground">
              {checkedListings.length > 0 && (
                <Command.Item className="p-0">
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start !bg-transparent pl-2">
                    <Link
                      viewTransition
                      to="/admin/reports/create"
                      state={{
                        listingIds: checkedListings,
                        filters: mapFilters,
                      }}>
                      <LucideSquareCheck className="mr-2 h-4 w-4 text-muted-foreground" />
                      From Selection
                    </Link>
                  </Button>
                </Command.Item>
              )}

              {mapViewState && !drawnFeature && (
                <Command.Item className="p-0">
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start !bg-transparent pl-2">
                    <Link
                      viewTransition
                      to="/admin/reports/create"
                      state={{
                        bounds: {
                          ne: [
                            mapViewState.bounds.east,
                            mapViewState.bounds.north,
                          ],
                          sw: [
                            mapViewState.bounds.west,
                            mapViewState.bounds.south,
                          ],
                        },
                        filters: mapFilters,
                      }}>
                      <LucideScan className="mr-2 h-4 w-4 text-muted-foreground" />
                      From Map Area
                    </Link>
                  </Button>
                </Command.Item>
              )}
              {drawnFeature && (
                <Command.Item className="p-0">
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start !bg-transparent pl-2">
                    <Link
                      viewTransition
                      to="/admin/reports/create"
                      state={{
                        feature: drawnFeature
                          ? {
                              geometry: drawnFeature.geometry,
                              type: drawnFeature.type,
                              id: drawnFeature.id,
                              properties: drawnFeature.properties,
                            }
                          : null,
                        filters: mapFilters,
                      }}>
                      {drawnFeature.properties?.isCircle ? (
                        <LucideCircleDotDashed className="mr-2 h-4 w-4 text-muted-foreground" />
                      ) : drawnFeature.properties?.isFreehand ? (
                        <LucideLineSquiggle className="mr-2 h-4 w-4 text-muted-foreground" />
                      ) : (
                        <LucideWaypoints className="mr-2 h-4 w-4 text-muted-foreground" />
                      )}
                      {drawnFeature.properties?.isCircle &&
                      drawnFeature.properties?.radiusInKm &&
                      drawnFeature.properties?.center
                        ? 'From Circle'
                        : drawnFeature.properties?.isFreehand
                          ? 'From Freehand'
                          : 'From Polygon'}
                    </Link>
                  </Button>
                </Command.Item>
              )}
            </Command.Group>

            {reports.length > 0 && checkedListings.length > 0 && (
              <Command.Group heading="Update" className="pt-0 text-foreground">
                <Command.Item className="h-11 p-0">
                  <ReportAddToExisting
                    reports={reports}
                    checkedListings={checkedListings}
                  />
                </Command.Item>
              </Command.Group>
            )}

            {checkedListings.length > 0 && (
              <>
                <Command.Group className="pt-0">
                  <Command.Separator className="my-1" />
                  <Command.Item className="p-0">
                    <CreatePdfDialog
                      listingIds={checkedListings}
                      filters={mapFilters}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start !bg-transparent pl-2">
                        <LucideFileText className="mr-2 h-4 w-4 text-muted-foreground" />
                        Download PDF
                      </Button>
                    </CreatePdfDialog>
                  </Command.Item>
                  <Command.Item className="p-0">
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start bg-transparent pl-2"
                      onClick={() => setIsOpen(false)}>
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        to={`/data/export.csv?listings=${checkedListings.join(',')}`}>
                        <LucideSheet className="mr-2 h-4 w-4 text-muted-foreground" />
                        Download CSV
                      </Link>
                    </Button>
                  </Command.Item>
                  {(mapFilters.statuses?.includes('sold') ||
                    mapFilters.statuses?.includes('leased')) && (
                    <Command.Item className="p-0">
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start bg-transparent pl-2"
                        onClick={() => setIsOpen(false)}>
                        <Link
                          target="_blank"
                          rel="noopener noreferrer"
                          to={exportTransactionsLink}>
                          <LucideSheet className="mr-2 h-4 w-4 text-muted-foreground" />
                          Download Transactions
                        </Link>
                      </Button>
                    </Command.Item>
                  )}
                  {/* </Command.Group>
                <Command.Group className="pt-0"> */}
                </Command.Group>
              </>
            )}

            <>
              <Command.Group className="pt-0">
                <Command.Separator className="my-1" />
                <Command.Item className="p-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start !bg-transparent pl-2"
                    onClick={() => {
                      const allIds = listings
                        .slice(0, 60)
                        .map((listing) => listing._id)
                      setCheckedListings([
                        ...new Set([...checkedListings, ...allIds]),
                      ])
                    }}>
                    <LucideSquareCheckBig className="mr-2 h-4 w-4 text-muted-foreground" />
                    Select All
                    {currentMapCount && currentMapCount > 60
                      ? ' (up to 60)'
                      : ''}
                  </Button>
                </Command.Item>
                {checkedListings.length > 0 && (
                  <Command.Item className="p-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start !bg-transparent pl-2"
                      onClick={() => {
                        // setDrawnFeature(null)
                        setCheckedListings([])
                        setIsOpen(false)
                      }}>
                      <LucideSquare className="mr-2 h-4 w-4 text-muted-foreground" />
                      Clear Selected
                    </Button>
                  </Command.Item>
                )}
              </Command.Group>
            </>

            {(checkedListings.length > 0 || hiddenListings.length > 0) && (
              <Command.Group className="pt-0">
                <Command.Separator className="my-1" />
                {hiddenListings.length > 0 && (
                  <Command.Item className="p-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start !bg-transparent pl-2"
                      onClick={() => {
                        setHiddenListings([])
                      }}>
                      <LucideEye className="mr-2 h-4 w-4 text-muted-foreground" />
                      {hiddenListings.length === 1
                        ? 'Unhide 1 Listing'
                        : `Unhide ${hiddenListings.length} Listings`}
                    </Button>
                  </Command.Item>
                )}
                {checkedListings.length > 0 && (
                  <Command.Item className="p-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start !bg-transparent pl-2"
                      onClick={() => {
                        setIsOpen(false)
                        setHiddenListings((prev) => {
                          const merged = prev.concat(checkedListings)
                          return Array.from(new Set(merged))
                        })
                        setCheckedListings([])
                      }}>
                      <LucideEyeOff className="mr-2 h-4 w-4 text-muted-foreground" />
                      Hide Selected Listings
                    </Button>
                  </Command.Item>
                )}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </Popover.Content>
    </Popover>
  )
}
